// Activities tab top-level panel — FE-SCRM-024.
//
// Owns the URL-driven filter + sort state, the activity list query,
// the composer modal, and the row-level action mutations. Mounted in
// SalesCrmPage.tsx via the Activities tab.

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  DashboardErrorState, DashboardLoadingState,
} from "@/features/dashboard";

import { ActivityComposerModal } from "./ActivityComposerModal";
import { ActivityFiltersBar } from "./ActivityFiltersBar";
import { ActivityListTable } from "./ActivityListTable";
import { QuickAddActivityMenu } from "./QuickAddActivityMenu";
import {
  useActivityListQuery,
  useCancelActivityMutation,
  useCompleteActivityMutation,
  useCreateActivityMutation,
  useDeleteActivityMutation,
  useRevertActivityMutation,
} from "./hooks";
import {
  readActivitiesStateFromUrl, writeActivitiesStateToParams,
} from "./urlState";
import type {
  ActivityCreateInput, ActivityListQuery, ActivityType, SalesActivity, SortKey,
} from "./types";


export function ActivitiesPanel() {
  const [search, setSearch] = useSearchParams();
  const urlState = useMemo(
    () => readActivitiesStateFromUrl(search), [search],
  );

  const [composer_open, setComposerOpen] = useState<ActivityType | null>(
    null,
  );

  // ── Data ──────────────────────────────────────────────────────
  const list_query = useActivityListQuery(urlState.filters);

  const create_mutation = useCreateActivityMutation();
  const complete_mutation = useCompleteActivityMutation();
  const revert_mutation = useRevertActivityMutation();
  const cancel_mutation = useCancelActivityMutation();
  const delete_mutation = useDeleteActivityMutation();

  // Sort client-side — the BE list endpoint defaults to -created_at;
  // sorting here keeps the URL the canonical source of truth without
  // an extra BE parameter.
  const rows_sorted = useMemo(() => {
    const rows = list_query.data?.rows ?? [];
    return sortActivities(rows, urlState.sort);
  }, [list_query.data, urlState.sort]);

  const pending_id = (
    complete_mutation.variables?.activity_id
    ?? (typeof revert_mutation.variables === "string"
      ? revert_mutation.variables : null)
    ?? (typeof cancel_mutation.variables === "string"
      ? cancel_mutation.variables : null)
    ?? (typeof delete_mutation.variables === "string"
      ? delete_mutation.variables : null)
    ?? null
  );

  // ── Handlers ──────────────────────────────────────────────────
  function updateState(next: Partial<{
    filters: ActivityListQuery; sort: SortKey;
  }>) {
    const merged = {
      filters: next.filters ?? urlState.filters,
      sort: next.sort ?? urlState.sort,
    };
    setSearch(writeActivitiesStateToParams(merged, search), { replace: true });
  }

  function handleQuickAdd(type: ActivityType | null) {
    setComposerOpen(type ?? "follow_up");
  }

  function handleComposerSubmit(input: ActivityCreateInput) {
    create_mutation.mutate(input, {
      onSuccess: () => setComposerOpen(null),
    });
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div
      data-testid="activities-panel"
      className="flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-text-primary">
            Sales Activities
          </h2>
          <p
            data-testid="activities-panel-hint"
            className="text-xs text-text-tertiary"
          >
            Sales motion — calls, meetings, RFPs, BOR letters.
            Distinct from operational Commitments in the Commitment Tracker.
          </p>
        </div>
        <QuickAddActivityMenu on_pick={handleQuickAdd} />
      </div>

      <ActivityFiltersBar
        filters={urlState.filters}
        sort={urlState.sort}
        on_change={updateState}
      />

      {list_query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : list_query.isError ? (
        <DashboardErrorState
          title="Couldn't load Sales Activities"
          description="Refresh to retry, or check the network connection."
          retry={() => list_query.refetch()}
        />
      ) : (
        <ActivityListTable
          rows={rows_sorted}
          is_pending_id={pending_id}
          on_complete={(a) =>
            complete_mutation.mutate({ activity_id: a.activity_id })
          }
          on_revert={(a) => revert_mutation.mutate(a.activity_id)}
          on_cancel={(a) => cancel_mutation.mutate(a.activity_id)}
          on_delete={(a) => delete_mutation.mutate(a.activity_id)}
          on_quick_add={handleQuickAdd}
        />
      )}

      {composer_open !== null && (
        <ActivityComposerModal
          initial_type={composer_open}
          is_submitting={create_mutation.isPending}
          on_submit={handleComposerSubmit}
          on_cancel={() => setComposerOpen(null)}
        />
      )}
    </div>
  );
}


// Exported for testing in isolation.
export function sortActivities(
  rows: SalesActivity[], sort: SortKey,
): SalesActivity[] {
  const out = [...rows];
  const negate = sort.startsWith("-");
  const key = (negate ? sort.slice(1) : sort) as "due_at" | "created_at";

  out.sort((a, b) => {
    const av = a[key] ?? "";
    const bv = b[key] ?? "";
    // Rows missing the sort field sink to the bottom regardless of
    // direction — producers shouldn't have nulls bubble to page one.
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return negate ? -cmp : cmp;
  });
  return out;
}
